import { Injectable, inject } from '@angular/core';
import { PresignedUploadModel } from '../../models/presigned-upload.model';
import { UploadedFileModel } from '../../models/uploaded-file.model';
import {
  PresignedUploadRequestDto,
  PresignedUploadResponseDto,
  ConfirmUploadRequestDto,
  ConfirmUploadResponseDto,
  FilenameSanitizerService
} from '@shared';
import { GetAttachmentUploadUrlResponseDto, ConfirmAttachmentUploadResponseDto } from '@features/cases';

/**
 * Mapper for transforming Files DTOs to domain entities and vice versa.
 */
@Injectable({ providedIn: 'root' })
export class FilesMapper {
  private readonly _sanitizer = inject(FilenameSanitizerService);
  /**
   * Maps File to presigned upload request DTO.
   * Sanitizes filename to prevent XSS and path traversal attacks.
   */
  public toPresignedUploadRequestDto(file: File): PresignedUploadRequestDto {
    return {
      fileName: this._sanitizer.sanitize(file.name),
      fileSizeBytes: file.size,
      contentType: file.type,
    };
  }

  /**
   * Maps presigned upload response DTO to domain entity
   */
  public toPresignedUploadDomain(dto: PresignedUploadResponseDto): PresignedUploadModel {
    const entity = new PresignedUploadModel();
    entity.uploadUrl = dto.uploadUrl;
    entity.fileId = dto.uploadId; // Backend returns 'uploadId'
    entity.expiresIn = this._calculateExpiresIn(dto.expiresAt);
    entity.s3Key = dto.s3Key; // Store S3 key for confirm step
    entity.requiredHeaders = dto.requiredHeaders || {}; // Headers for S3 PUT
    return entity;
  }

  /**
   * Calculate expires in seconds from ISO date string
   */
  private _calculateExpiresIn(expiresAt: string): number {
    const expiresDate = new Date(expiresAt);
    const now = new Date();
    return Math.floor((expiresDate.getTime() - now.getTime()) / 1000);
  }

  /**
   * Maps uploadId to confirm upload request DTO
   */
  public toConfirmUploadRequestDto(uploadId: string): ConfirmUploadRequestDto {
    return {
      uploadId, // Backend expects 'uploadId' (Guid)
    };
  }

  /**
   * Maps confirm upload response DTO to domain entity.
   * Backend only returns attachment ID, so we need original file metadata.
   * Sanitizes filename to prevent XSS when displayed.
   *
   * @param dto - Backend response with attachment ID
   * @param file - Original file for metadata
   * @param s3Key - S3 key from presigned upload
   */
  public toUploadedFileDomain(dto: ConfirmUploadResponseDto, file: File, s3Key: string): UploadedFileModel {
    const entity = new UploadedFileModel();
    entity.fileId = dto.id;
    entity.fileUrl = s3Key;
    entity.fileName = this._sanitizer.sanitize(file.name);
    entity.fileSizeBytes = file.size;
    entity.contentType = file.type;
    return entity;
  }

  /**
   * Maps CasesApi presigned upload response DTO to domain entity.
   * Uses the same structure as toPresignedUploadDomain but for Case attachments.
   */
  public toCasePresignedUploadDomain(dto: GetAttachmentUploadUrlResponseDto): PresignedUploadModel {
    const entity = new PresignedUploadModel();
    entity.uploadUrl = dto.uploadUrl;
    entity.fileId = dto.uploadId;
    entity.expiresIn = this._calculateExpiresIn(dto.expiresAt);
    entity.s3Key = dto.s3Key;
    entity.requiredHeaders = dto.requiredHeaders || {};
    return entity;
  }

  /**
   * Maps CasesApi confirm upload response to domain entity.
   * Overload for case attachment confirmation.
   */
  public toCaseUploadedFileDomain(dto: ConfirmAttachmentUploadResponseDto, file: File, s3Key: string): UploadedFileModel {
    const entity = new UploadedFileModel();
    entity.fileId = dto.id;
    entity.fileUrl = s3Key;
    entity.fileName = this._sanitizer.sanitize(file.name);
    entity.fileSizeBytes = file.size;
    entity.contentType = file.type;
    return entity;
  }
}
